import { ActionSheetIOS, Alert, Linking, Platform } from 'react-native';

type MapsOption = {
  label: string;
  url: string;
};

function buildUrls(lat: number, lng: number) {
  const destination = `${lat},${lng}`;

  return {
    apple: `http://maps.apple.com/?daddr=${destination}&dirflg=d`,
    google: `comgooglemaps://?daddr=${destination}&directionsmode=driving`,
    googleWeb: `https://www.google.com/maps/dir/?api=1&destination=${destination}`,
    waze: `waze://?ll=${destination}&navigate=yes`,
  };
}

function buildAddressUrls(address: string) {
  const destination = encodeURIComponent(address.trim());

  return {
    apple: `http://maps.apple.com/?daddr=${destination}&dirflg=d`,
    google: `comgooglemaps://?daddr=${destination}&directionsmode=driving`,
    googleWeb: `https://www.google.com/maps/dir/?api=1&destination=${destination}`,
    waze: `waze://?q=${destination}&navigate=yes`,
  };
}

function canOpenURL(url: string) {
  return Linking.canOpenURL(url).catch(() => false);
}

export function openAddressInMaps(address: string) {
  const encoded = encodeURIComponent(address.trim());
  if (!encoded) {
    return Promise.resolve();
  }

  if (Platform.OS === 'web') {
    return Linking.openURL(`https://maps.google.com/?q=${encoded}`).catch(() => undefined);
  }

  const appleURL = `maps://maps.apple.com/?q=${encoded}`;
  const webURL = `https://maps.apple.com/?q=${encoded}`;

  if (Platform.OS === 'ios') {
    return Linking.openURL(appleURL).catch(() => Linking.openURL(webURL).catch(() => undefined));
  }

  return Linking.openURL(`geo:0,0?q=${encoded}`).catch(() =>
    Linking.openURL(`https://maps.google.com/?q=${encoded}`).catch(() => undefined)
  );
}

async function openMapsChooser(
  urls: ReturnType<typeof buildUrls> | ReturnType<typeof buildAddressUrls>
) {
  if (Platform.OS === 'web') {
    Linking.openURL(urls.googleWeb).catch(() => undefined);
    return;
  }

  const available: MapsOption[] = [];

  if (Platform.OS === 'ios') {
    available.push(
      { label: 'Apple Maps', url: urls.apple },
      { label: 'Google Maps', url: urls.googleWeb }
    );
  }

  const [hasGoogle, hasWaze] = await Promise.all([canOpenURL(urls.google), canOpenURL(urls.waze)]);

  if (hasGoogle && Platform.OS === 'ios') {
    available[1] = { label: 'Google Maps', url: urls.google };
  } else if (hasGoogle) {
    available.push({ label: 'Google Maps', url: urls.google });
  }
  if (hasWaze) available.push({ label: 'Waze', url: urls.waze });

  if (Platform.OS !== 'ios' && available.length === 1) {
    Linking.openURL(available[0].url).catch(() => undefined);
    return;
  }

  // If none found (shouldn't happen on iOS), fall back to Google web
  if (available.length === 0) {
    Linking.openURL(urls.googleWeb).catch(() => undefined);
    return;
  }

  if (Platform.OS === 'ios') {
    ActionSheetIOS.showActionSheetWithOptions(
      {
        options: [...available.map((o) => o.label), 'Cancel'],
        cancelButtonIndex: available.length,
        title: 'Open directions in…',
      },
      (index) => {
        if (index < available.length) {
          Linking.openURL(available[index].url).catch(() => undefined);
        }
      }
    );
  } else {
    // Android: Alert with buttons
    Alert.alert('Open directions in…', undefined, [
      ...available.map((opt) => ({
        text: opt.label,
        onPress: () => Linking.openURL(opt.url).catch(() => undefined),
      })),
      { text: 'Cancel', style: 'cancel' as const },
    ]);
  }
}

export function openDirections(lat: number, lng: number) {
  return openMapsChooser(buildUrls(lat, lng));
}

export function openAddressDirections(address: string) {
  const trimmed = address.trim();
  if (!trimmed) {
    return Promise.resolve();
  }

  return openMapsChooser(buildAddressUrls(trimmed));
}
